import { getWorkerUrl } from '../../utils/workerUrl';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { supabase } from '../../supabaseClient';
import { formatDistanceToNow } from 'date-fns';
import ReactDOM from 'react-dom';



export default function AffiliatePayoutGrid() {
  const [transactions, setTransactions] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('CONNECTING'); // CONNECTING, SUBSCRIBED, TIMED_OUT, CLOSED, DISCONNECTED
  const [recentTxIds, setRecentTxIds] = useState(new Set());
  const reconnectCountRef = React.useRef(0);
  const [manualReconnectTrigger, setManualReconnectTrigger] = useState(0);
  const [selectedTokenFilter, setSelectedTokenFilter] = useState('All Tokens');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTx, setSelectedTx] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [toastMessage, setToastMessage] = useState(null);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setToastMessage(`Copied ${type} to clipboard`);
    setTimeout(() => setToastMessage(null), 2000);
  };


  useEffect(() => {
    window.dispatchEvent(new CustomEvent('realtime-status-update', { detail: connectionStatus }));
  }, [connectionStatus]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedTx(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    let channel;
    let retryTimeout;
    const maxBackoff = 30000;

    const fetchInitialData = async () => {
      const { data, error } = await supabase
        .from('blockchain_transactions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);

      if (data) {
        setTransactions(data.map(mapTransaction));
      }
    };

    const subscribeToChanges = () => {
      if (channel) {
        supabase.removeChannel(channel);
      }

      setConnectionStatus('CONNECTING');

      channel = supabase
        .channel('ledger-realtime')
        .on('postgres_changes', { event: '*', schema: 'public', table: 'blockchain_transactions' }, (payload) => {
          setTransactions(prev => {
            if (payload.eventType === 'INSERT') {
              setRecentTxIds(prev => {
                const next = new Set(prev);
                next.add(payload.new.id);
                return next;
              });
              setToastMessage('New settlement recorded');
              setTimeout(() => setToastMessage(null), 2000);
              setTimeout(() => {
                setRecentTxIds(prev => {
                  const next = new Set(prev);
                  next.delete(payload.new.id);
                  return next;
                });
              }, 2500);
              // Small timeouts on unmount are benign, cleanup not strictly necessary here.
              return [mapTransaction(payload.new), ...prev].slice(0, 10);
            }
            if (payload.eventType === 'UPDATE') {
               return prev.map(tx => tx.id === payload.new.id ? mapTransaction(payload.new) : tx);
            }
            return prev;
          });
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            setConnectionStatus(status);
            reconnectCountRef.current = 0; // Reset exponential backoff counter on successful subscription
          } else if (status === 'TIMED_OUT' || status === 'CLOSED' || status === 'CHANNEL_ERROR') {
            if (reconnectCountRef.current >= 5) {
              setConnectionStatus('DISCONNECTED');
            } else {
              setConnectionStatus(status);
              // Implement exponential backoff for ledger-realtime channel
              // This prevents silent stream drops from freezing the live payout grid
              const delay = Math.min(1000 * Math.pow(2, reconnectCountRef.current), maxBackoff);
              reconnectCountRef.current++;
              clearTimeout(retryTimeout);
              retryTimeout = setTimeout(() => {
                subscribeToChanges();
              }, delay);
            }
          }
        });
    };

    fetchInitialData();
    subscribeToChanges();

    return () => {
      clearTimeout(retryTimeout);
      if (channel) supabase.removeChannel(channel);
    };
  }, [manualReconnectTrigger]);

  const mapTransaction = (dbTx) => ({
    id: dbTx.id,
    partner: dbTx.partner_id ? `AXM-${dbTx.partner_id.substring(0,3)}` : 'Unknown', // mocking partner ID format
    wallet: `${dbTx.wallet_address.substring(0, 5)}...${dbTx.wallet_address.substring(dbTx.wallet_address.length - 3)}`,
    fullWallet: dbTx.wallet_address,
    txHash: dbTx.transaction_hash || '',
    amount: Number(dbTx.amount),
    currency: dbTx.currency,
    status: dbTx.status,
    time: formatDistanceToNow(new Date(dbTx.updated_at || dbTx.created_at || Date.now()), { addSuffix: true })
  });

  const getStatusConfig = (status) => {
    switch(status) {
      case 'minted': return { color: 'text-emerald-400', bg: 'bg-emerald-400/10', border: 'border-emerald-400/20', icon: 'CheckCircle' };
      case 'pending': return { color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/20', icon: 'Clock' };
      case 'failed': return { color: 'text-rose-400', bg: 'bg-rose-400/10', border: 'border-rose-400/20', icon: 'XCircle' };
      default: return { color: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-slate-400/20', icon: 'HelpCircle' };
    }
  };

  return (
        <div className="glass-panel hud-border rounded-xl overflow-hidden flex flex-col h-full relative">
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 10, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className="absolute top-0 left-1/2 z-50 px-4 py-2 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-full text-sm font-medium shadow-lg backdrop-blur-md"
          >
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
      <div className="p-6 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <SafeIcon name="Layers" className="text-emerald-500" />
            Thirdweb Ledger Sync
            <div className="flex items-center ml-3 gap-2">
              <div
                className={`w-2.5 h-2.5 rounded-full ${
                  connectionStatus === 'SUBSCRIBED' ? 'bg-emerald-500' :
                  connectionStatus === 'CONNECTING' ? 'bg-amber-500 animate-pulse' :
                  'bg-rose-500'
                }`}
                title={`Connection Status: ${connectionStatus}`}
              />
              {connectionStatus === 'DISCONNECTED' && (
                <button
                  onClick={() => {
                    reconnectCountRef.current = 0;
                    setManualReconnectTrigger(prev => prev + 1);
                  }}
                  className="text-[10px] px-2 py-0.5 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded hover:bg-rose-500/20 transition-colors"
                >
                  Reconnect Socket
                </button>
              )}
            </div>
          </h2>
          <p className="text-slate-400 text-sm mt-1">Real-time smart contract settlements</p>
        </div>
        <div className="flex flex-wrap items-center gap-4 w-full lg:w-auto">
                    {(selectedTokenFilter !== 'All Tokens' || searchQuery.trim() !== '') && (
            <button
              onClick={() => {
                setSelectedTokenFilter('All Tokens');
                setSearchQuery('');
              }}
              className="text-[10px] uppercase font-bold tracking-wider px-2 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/30 rounded hover:bg-amber-500/20 transition-colors"
            >
              Clear Filters
            </button>
          )}
          <input
            id="affiliate-search-input"
            aria-label="Search Transaction Hash or Recipient"
            autoComplete="off"
            type="text"
            placeholder="Search Tx Hash or Recipient..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="text-sm px-3 py-2 bg-slate-900/80 backdrop-blur-md shadow-[0_0_15px_rgba(16,185,129,0.08)] border border-emerald-500/20 backdrop-blur-sm text-zinc-100 rounded-lg border border-zinc-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-colors placeholder-zinc-500 w-64 shadow-inner"
          />
          <select
            value={selectedTokenFilter}
            onChange={(e) => setSelectedTokenFilter(e.target.value)}
            className="text-sm px-3 py-2 bg-slate-900/80 backdrop-blur-md shadow-[0_0_15px_rgba(16,185,129,0.08)] border border-emerald-500/20 backdrop-blur-sm text-zinc-100 rounded-lg border border-zinc-700 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 transition-colors shadow-inner"
          >
            <option value="All Tokens">All Tokens</option>
            <option value="ETH">ETH</option>
            <option value="USDC">USDC</option>
            <option value="SOL">SOL</option>
            <option value="MATIC">MATIC</option>
          </select>

          <button
            onClick={async () => {
              const pendingPayouts = transactions.filter(tx => tx.status === 'pending');
              if (pendingPayouts.length === 0) {
                 alert("No pending payouts to settle.");
                 return;
              }
              const totalAmount = pendingPayouts.reduce((sum, tx) => sum + (Number(tx.amount) || 0), 0);

              if (window.confirm(`Settle ${pendingPayouts.length} approved payouts totaling $${totalAmount.toLocaleString()}?`)) {
                 try {
                   setIsLoading(true);
                   const workerUrl = 'http://localhost:8787'; // fallback or import.meta.env.VITE_EDGE_WORKER_URL
                   // Wait, getWorkerUrl() is used below? No, it needs import.
                   // I will use getWorkerUrl() since it should be available.

                   const payoutsPayload = pendingPayouts.map(tx => ({
                      recipient_address: tx.wallet,
                      amount_usdc: tx.amount,
                      partner_id: tx.partner,
                      id: tx.id
                   }));

                   // Assuming getWorkerUrl is imported in this file. Let's check imports.
                   const res = await fetch(`${getWorkerUrl()}/api/v1/treasury/batch-payout`, {
                      method: "POST",
                      headers: {
                         "Content-Type": "application/json",
                         "X-Axim-Signature": import.meta.env.VITE_AXIM_INTERNAL_KEY || ""
                      },
                      body: JSON.stringify({ payouts: payoutsPayload })
                   });

                   if (res.status === 202) {
                      const data = await res.json();
                      alert(data.message || "Awaiting multi-sig approval");
                   } else if (!res.ok) {
                      const err = await res.json();
                      if (res.status === 429 && err.status === "GAS_SURGE_PAUSED") {
                         alert(`Gas Surge Detected (${err.current_gas_gwei} Gwei). Pausing execution. Retry in ${err.retry_after_sec}s.`);
                      } else {
                         alert(`Failed: ${err.error}`);
                      }
                   } else {
                      const data = await res.json();
                      alert(`Successfully settled ${data.processed_count} payouts. Tx: ${data.transaction_hash}`);
                      // Mark locally as settled
                      setTransactions(prev => prev.map(tx => {
                         if (pendingPayouts.find(p => p.id === tx.id)) {
                             return { ...tx, status: 'minted', txHash: data.transaction_hash };
                         }
                         return tx;
                      }));
                   }
                 } catch (e) {
                   alert("Execution failed: " + e.message);
                 } finally {
                   setIsLoading(false);
                 }
              }
            }}
            className="text-sm font-bold uppercase tracking-wider px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-indigo-900/20"
          >
            <SafeIcon name="CheckCircle" className="w-4 h-4" />
            Settle All Approved Payouts
          </button>
          <button
onClick={() => {
              const headers = ['id', 'created_at', 'amount', 'token_type', 'status', 'transaction_hash'];
              const csvRows = [];
              csvRows.push(headers.join(','));

              const currentFilteredTransactions = transactions.filter(tx => {
                const matchesToken = selectedTokenFilter === 'All Tokens' || tx.token === selectedTokenFilter;
                const matchesSearch = tx.wallet.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                      (tx.transaction_hash && tx.transaction_hash.toLowerCase().includes(searchQuery.toLowerCase())) ||
                                      tx.partner.toLowerCase().includes(searchQuery.toLowerCase());
                return matchesToken && matchesSearch;
              });
              for (const tx of currentFilteredTransactions) {
                const row = [
                  tx.id,
                  tx.created_at || new Date().toISOString(), // Mocking created_at if not present in mapped obj
                  tx.amount,
                  tx.token,
                  tx.status,
                  tx.transaction_hash || 'N/A'
                ];
                // Escape quotes and wrap in quotes for robust CSV
                const escapedRow = row.map(cell => `"${String(cell).replace(/"/g, '""')}"`);
                csvRows.push(escapedRow.join(','));
              }

              const csvString = csvRows.join('\n');
              const blob = new Blob([csvString], { type: 'text/csv' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `green-machine-payouts-${new Date().toISOString()}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }}
            className="text-sm font-bold uppercase tracking-wider px-3 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-emerald-900/20"
          >
            <SafeIcon name="Download" className="w-4 h-4" />
            Download CSV
          </button>

        </div>
      </div>

      <div className="overflow-x-auto flex-1">
        <table className="w-full text-left text-sm whitespace-nowrap transition-all duration-300">
          <thead className="bg-zinc-900/80 backdrop-blur-md text-zinc-400 shadow-sm border-b border-zinc-800">
            <tr>
              <th className="px-6 py-4 font-medium">Partner ID</th>
              <th className="px-6 py-4 font-medium">Wallet Destination</th>
              <th className="px-6 py-4 font-medium">Amount</th>
              <th className="px-6 py-4 font-medium">Status</th>
              <th className="px-6 py-4 font-medium text-right">Time</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/50">
            {!isLoading && transactions.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-slate-500 italic">
                  No recent settlements located
                </td>
              </tr>
            ) : isLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <tr key={idx} className="animate-pulse">
                  <td className="px-6 py-4"><div className="h-4 bg-slate-700/50 rounded w-16"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-slate-700/50 rounded w-24"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-slate-700/50 rounded w-16"></div></td>
                  <td className="px-6 py-4"><div className="h-6 bg-slate-700/50 rounded w-20"></div></td>
                  <td className="px-6 py-4 flex justify-end"><div className="h-4 bg-slate-700/50 rounded w-20"></div></td>
                </tr>
              ))
            ) : (
              <AnimatePresence mode="popLayout">
                {transactions.filter(tx => {
                  const matchesToken = selectedTokenFilter === 'All Tokens' || tx.currency === selectedTokenFilter;
                  if (!searchQuery) return matchesToken;
                  const query = searchQuery.toLowerCase();
                  const matchesSearch = (tx.txHash && tx.txHash.toLowerCase().includes(query)) ||
                                        (tx.fullWallet && tx.fullWallet.toLowerCase().includes(query));
                  return matchesToken && matchesSearch;
                }).map((tx) => {
                  const config = getStatusConfig(tx.status);
                  return (
                    <motion.tr
                      key={tx.id}
                      layout
                      initial={{ opacity: 0, y: 10 }}
                      animate={{
                        opacity: 1,
                        y: 0,
                        backgroundColor: tx.status === 'minted' ? ['rgba(16, 185, 129, 0.4)', 'rgba(30, 41, 59, 0)'] : 'rgba(30, 41, 59, 0)'
                      }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      className={`hover:bg-slate-800/30 transition-colors cursor-pointer ${recentTxIds.has(tx.id) ? 'bg-emerald-500/10 border border-emerald-500/30 animate-pulse' : ''}`}
                      onClick={() => setSelectedTx(tx)}
                    >
                      <td className="px-6 py-4 font-mono text-emerald-400">{tx.partner}</td>
                      <td className="px-6 py-4 font-mono text-slate-300">
                        {tx.wallet}
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(tx.fullWallet, 'wallet'); }}
                          className="ml-2 text-slate-500 hover:text-emerald-400 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <SafeIcon name="Copy" className="w-3 h-3 inline" />
                        </button>
                      </td>
                      <td className="px-6 py-4 font-semibold text-white">
                        ${tx.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })} <span className="text-slate-500 font-normal">{tx.currency}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-xs font-medium ${config.bg} ${config.color} ${config.border}`}>
                          <SafeIcon name={config.icon} className="w-3.5 h-3.5" />
                          {tx.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-slate-400">
                        {tx.time}
                        {tx.txHash && (
                          <button
                            onClick={(e) => { e.stopPropagation(); copyToClipboard(tx.txHash, 'transaction hash'); }}
                            className="ml-2 text-slate-500 hover:text-emerald-400 transition-colors"
                            title="Copy TxHash"
                          >
                            <SafeIcon name="Link" className="w-3 h-3 inline" />
                          </button>
                        )}
                      </td>
                    </motion.tr>
                  );
                })}
              </AnimatePresence>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}